import {
	Box,
	Button,
	Dialog,
	Field,
	Flex,
	Input,
	Portal,
	Select,
	Separator,
	Show,
	Stack,
	Switch,
	Text,
} from '@welbe/design-system';
import { tokens } from '@welbe/web-design-system';
import { FiInfo } from 'react-icons/fi';
import { useAdminDialog } from './AdminDialog.hooks';
import { AdminDialogProps } from './AdminDialog.types';

export const AdminDialog = ({
	open,
	setOpen,
	user,
	onSuccess,
}: AdminDialogProps): JSX.Element => {
	const {
		isEditingAdmin,
		fieldsWithErrors,
		isRequestLoading,
		admin,
		setAdmin,
		handleChangeAdmin,
		roles,
		isRolesLoading,
		handleSaveAdmin,
	} = useAdminDialog({ open, setOpen, user, onSuccess });

	return (
		<Dialog.Root
			color={tokens.color_gray_dark}
			lazyMount
			open={open}
			onOpenChange={(details: { open: boolean }) => setOpen(details.open)}
		>
			<Portal>
				<Dialog.Backdrop />
				<Dialog.Positioner>
					<Dialog.Content
						animation="slideIn"
						width={{ base: 'full', md: '900px' }}
						maxWidth="95vw"
					>
						<Dialog.Header>
							<Flex gap="3" alignItems="center">
								<Box
									w="38px"
									h="38px"
									bg="blue.500"
									borderRadius="md"
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<FiInfo size={20} color="white" />
								</Box>
								<Stack gap="0">
									<Dialog.Title>
										<Show
											when={isEditingAdmin}
											fallback="Agregar personal administrativo"
										>
											Actualizar personal administrativo
										</Show>
									</Dialog.Title>
									<Text color="gray.600" fontSize="14px">
										Registra los datos del personal administrativo de tu
										organización
									</Text>
								</Stack>
							</Flex>
						</Dialog.Header>
						<Separator />
						<Dialog.Body padding="1.5rem">
							<Text fontSize={16} fontWeight={500}>
								Datos personales
							</Text>
							<Flex direction="column" gap={4} margin="1.5rem 0">
								<Field.Root
									invalid={!!fieldsWithErrors['name']}
									disabled={isRequestLoading}
									required
								>
									<Field.Label>
										Nombre <Field.RequiredIndicator />
									</Field.Label>
									<Input
										placeholder="Escribe el nombre"
										background="white"
										name="name"
										required
										disabled={isRequestLoading}
										value={admin.name}
										onChange={(event) => handleChangeAdmin(event)}
									/>
									<Field.ErrorText>{fieldsWithErrors['name']}</Field.ErrorText>
								</Field.Root>
								<Field.Root
									invalid={!!fieldsWithErrors['email']}
									disabled={isRequestLoading}
									required
								>
									<Field.Label>
										Correo <Field.RequiredIndicator />
									</Field.Label>
									<Input
										placeholder="Escribe el correo"
										background="white"
										name="email"
										required
										disabled={isRequestLoading}
										value={admin.email}
										onChange={(event) => handleChangeAdmin(event)}
									/>
									<Field.ErrorText>{fieldsWithErrors['email']}</Field.ErrorText>
								</Field.Root>
								<Select.Snippet
									disabled={isRequestLoading || roles.length === 0}
									label="Perfil de usuario"
									placeholder="Seleccione sucursales"
									invalid={!!fieldsWithErrors['roleId']}
									required
									loading={isRolesLoading}
									controlProps={{ border: '0 none' }}
									showEmpty
									onValueChange={(data: any) =>
										setAdmin({
											...admin,
											roleId: data?.id,
										})
									}
									value={[admin.roleId]}
									options={roles}
									errorText={fieldsWithErrors['roleId']}
								/>
							</Flex>

							<Show when={isEditingAdmin} fallback={null}>
								<Switch.Snippet
									disabled={isRequestLoading}
									name="active"
									checked={admin.active}
									onCheckedChange={(data: { checked: boolean }) =>
										setAdmin({
											...admin,
											active: data.checked,
										})
									}
								>
									Usuario activo
								</Switch.Snippet>
							</Show>
						</Dialog.Body>
						<Dialog.Footer
							padding="1.5rem"
							borderTop="1px solid"
							borderColor="gray.200"
							gap={2}
						>
							<Button
								variant="outline"
								onClick={() => setOpen(false)}
								disabled={isRequestLoading}
							>
								Cancelar
							</Button>
							<Button onClick={handleSaveAdmin} loading={isRequestLoading}>
								Guardar y salir
							</Button>
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};
